<template>
  <component :is="embedded ? 'div' : 'v-main'">
    <component :is="embedded ? 'div' : 'v-container'" v-bind="embedded ? {} : { fluid: true }">
      <v-overlay :value="isLoading" z-index="999" :absolute="embedded">
        <v-progress-circular indeterminate size="64" color="primary"></v-progress-circular>
      </v-overlay>

      <component :is="embedded ? 'div' : 'v-card'" v-bind="embedded ? {} : { elevation: 6, rounded: 'xxl' }" :class="embedded ? '' : 'app-card'" v-if="assignment">
        <v-card-title v-if="!embedded">
          <v-btn class="mx-2" fab x-small color="primary" @click="$router.back()">
            <v-icon dark>mdi-arrow-left</v-icon> </v-btn
          >{{ assignment.assignment_number }}
          <v-chip class="ml-3" :color="statusColor(assignment.payment_status)" text-color="white">
            {{ statusLabels[assignment.payment_status] }}
          </v-chip>
          <v-spacer></v-spacer>
          <v-btn outlined color="primary" :loading="isDownloading" @click="downloadInvoice">
            <v-icon left small>mdi-file-download-outline</v-icon> Download Invoice
          </v-btn>
        </v-card-title>
        <component :is="embedded ? 'div' : 'v-card-text'">
          <div class="d-flex justify-end mb-4" v-if="embedded">
            <v-btn outlined color="primary" small :loading="isDownloading" @click="downloadInvoice">
              <v-icon left small>mdi-file-download-outline</v-icon> Download Invoice
            </v-btn>
          </div>

          <v-row dense>
            <v-col cols="6" sm="3">
              <AppInfoCard icon="mdi-account" label="Assigned To" :value="assignment.assigned_to ? `${assignment.assigned_to.first_name} ${assignment.assigned_to.last_name || ''}` : 'N/A'" />
            </v-col>
            <v-col cols="6" sm="3">
              <AppInfoCard icon="mdi-calendar" label="Assignment Date" :value="$options.filters.niceDate(assignment.assignment_date)" />
            </v-col>
            <v-col cols="6" sm="3">
              <AppInfoCard icon="mdi-calendar-clock" label="Due Date" :value="$options.filters.niceDate(assignment.due_date) || 'N/A'" />
            </v-col>
            <v-col cols="6" sm="3">
              <AppInfoCard icon="mdi-truck-delivery" label="Transporter" :value="assignment.transporter_manager ? `${assignment.transporter_manager.first_name} ${assignment.transporter_manager.last_name || ''}` : 'N/A'" />
            </v-col>
            <v-col cols="12" v-if="assignment.remarks">
              <AppInfoCard icon="mdi-note-text" label="Remarks" :value="assignment.remarks" />
            </v-col>
          </v-row>

          <!-- Items -->
          <AppSectionHeader label="Items Assigned" :count="assignment.items ? assignment.items.length : 0" />
          <v-simple-table v-if="assignment.items && assignment.items.length">
            <template v-slot:default>
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Variant</th>
                  <th class="text-right">Qty</th>
                  <th class="text-right">Unit Price</th>
                  <th class="text-right">Line Total</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in assignment.items" :key="`item_${item.id}`">
                  <td>{{ item.product ? item.product.name : "N/A" }}</td>
                  <td>{{ item.variant ? `${item.variant.color || ""} - ${item.variant.size || ""}` : "N/A" }}</td>
                  <td class="text-right mono-figure">{{ item.quantity }}</td>
                  <td class="text-right mono-figure">₹{{ item.unit_price }}</td>
                  <td class="text-right mono-figure">₹{{ item.line_total }}</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>

          <v-divider class="my-6"></v-divider>

          <v-row>
            <v-col sm="3">
              <div class="text-caption grey--text">Total</div>
              <div class="text-h6 mono-figure">₹{{ assignment.total_amount }}</div>
            </v-col>
            <v-col sm="3">
              <div class="text-caption grey--text">Paid</div>
              <div class="text-h6 mono-figure" style="color: var(--color-success)">₹{{ assignment.paid_amount }}</div>
            </v-col>
            <v-col sm="3">
              <div class="text-caption grey--text">Pending</div>
              <div class="text-h6 mono-figure" style="color: var(--color-error)">₹{{ assignment.pending_amount }}</div>
            </v-col>
          </v-row>

          <AppSectionHeader label="Record Payment" />
          <v-row align="center" v-if="Number(assignment.pending_amount) > 0">
            <v-col sm="2">
              <v-text-field
                v-model.number="payment.amount"
                type="number"
                min="0.01"
                label="Amount"
                outlined
                dense
                hide-details
                :error-messages="errors.amount"
              ></v-text-field>
            </v-col>
            <v-col sm="3">
              <v-menu
                v-model="paymentDateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="payment.payment_date"
                    outlined
                    readonly
                    dense
                    hide-details
                    label="Payment Date"
                    v-bind="attrs"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="payment.payment_date"
                  @input="paymentDateMenu = false"
                ></v-date-picker>
              </v-menu>
            </v-col>
            <v-col sm="2">
              <v-select
                v-model="payment.payment_mode"
                :items="paymentModeOptions"
                label="Mode"
                outlined
                dense
                hide-details
              ></v-select>
            </v-col>
            <v-col sm="3">
              <v-text-field
                v-model="payment.reference_number"
                label="Reference No."
                outlined
                dense
                hide-details
              ></v-text-field>
            </v-col>
            <v-col sm="2">
              <v-btn block color="primary" :loading="isRecording" @click="recordPayment">
                <v-icon left small>mdi-plus</v-icon> Add
              </v-btn>
            </v-col>
          </v-row>
          <div class="text-caption grey--text mt-2" v-else>
            This assignment is fully paid.
          </div>
          <div class="text-caption error--text mt-1" v-if="paymentError">
            {{ paymentError }}
          </div>

          <AppSectionHeader label="Payment History" />
          <v-simple-table v-if="assignment.payments && assignment.payments.length">
            <template v-slot:default>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Mode</th>
                  <th>Reference</th>
                  <th class="text-right">Amount</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="p in assignment.payments" :key="`payment_${p.id}`">
                  <td>{{ p.payment_date | niceDate }}</td>
                  <td>{{ paymentModeLabels[p.payment_mode] }}</td>
                  <td>{{ p.reference_number || "-" }}</td>
                  <td class="text-right mono-figure">₹{{ p.amount }}</td>
                  <td class="text-right">
                    <v-btn icon color="error" x-small @click="askDeletePayment(p.id)">
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
          <div class="text-caption grey--text" v-else>No payments recorded yet.</div>
        </component>
      </component>
    </component>

    <!-- Delete confirmation -->
    <ConfirmationDialog
      :show.sync="deleteDialog"
      title="Delete Payment"
      message="Are you sure you want to delete this payment? This action cannot be undone."
      icon="mdi-delete"
      color="error"
      :loading="isDeletingPayment"
      @confirm="deletePayment(pendingDeletePaymentId)"
    />
  </component>
</template>
<script>
import axios from "axios";
import ConfirmationDialog from "@/components/ConfirmationDialog.vue";
import AppInfoCard from "@/components/AppInfoCard.vue";
import AppSectionHeader from "@/components/AppSectionHeader.vue";
export default {
  name: "StockAssignmentIdPage",
  components: { ConfirmationDialog, AppInfoCard, AppSectionHeader },
  props: {
    // When true, this renders inside a dialog (opened from the Stock
    // Payments list on row click) instead of its own routed page.
    embedded: { type: Boolean, default: false },
    assignmentId: { type: String, default: null },
  },
  data() {
    return {
      assignment: null,
      payment: {
        amount: null,
        payment_date: new Date().toISOString().substr(0, 10),
        payment_mode: 0,
        reference_number: "",
      },
      paymentDateMenu: false,
      isLoading: false,
      isRecording: false,
      isDownloading: false,
      paymentError: "",
      statusLabels: {
        0: "Unpaid",
        1: "Partial",
        2: "Paid",
      },
      paymentModeLabels: {
        0: "Cash",
        1: "Bank Transfer",
        2: "Cheque",
        3: "UPI",
        4: "Other",
      },
      paymentModeOptions: [
        { text: "Cash", value: 0 },
        { text: "Bank Transfer", value: 1 },
        { text: "Cheque", value: 2 },
        { text: "UPI", value: 3 },
        { text: "Other", value: 4 },
      ],
      deleteDialog: false,
      pendingDeletePaymentId: null,
      isDeletingPayment: false,
    };
  },
  computed: {
    errors() {
      return this.$store.getters["errors/getErrors"] || {};
    },
  },
  mounted() {
    this.$store.dispatch("errors/setErrors", {});
    this.getData();
  },
  methods: {
    statusColor(status) {
      const colors = { 0: "error", 1: "warning", 2: "success" };
      return colors[status] || "grey";
    },
    async getData() {
      this.isLoading = true;
      let encryptedId = this.assignmentId || this.$route.params.id;
      await axios
        .get(`/stock_assignments/${encryptedId}`)
        .then((response) => {
          this.assignment = response.data.data;
        })
        .catch((error) => {
          console.error("Error fetching data:", error);
        });
      this.isLoading = false;
    },
    async recordPayment() {
      try {
        this.isRecording = true;
        this.paymentError = "";
        this.$store.dispatch("errors/setErrors", {});
        await axios.post("/stock_assignment_payments", {
          stock_assignment_id: this.assignment.id,
          amount: this.payment.amount,
          payment_date: this.payment.payment_date,
          payment_mode: this.payment.payment_mode,
          reference_number: this.payment.reference_number,
        });
        this.payment = {
          amount: null,
          payment_date: new Date().toISOString().substr(0, 10),
          payment_mode: 0,
          reference_number: "",
        };
        this.isRecording = false;
        await this.getData();
        this.$emit("saved");
      } catch (e) {
        console.log(e);
        this.isRecording = false;
        if (e.response && e.response.data && e.response.data.message) {
          this.paymentError = e.response.data.message;
        }
        if (e.response && e.response.data && e.response.data.errors) {
          this.$store.dispatch("errors/setErrors", e.response.data.errors);
        }
      }
    },
    askDeletePayment(id) {
      this.pendingDeletePaymentId = id;
      this.deleteDialog = true;
    },
    async deletePayment(id) {
      this.isDeletingPayment = true;
      await axios
        .post(`/stock_assignment_payments/delete/${this.encryptIt(id)}`)
        .then(() => {
          this.deleteDialog = false;
          this.getData();
          this.$emit("saved");
        })
        .catch((error) => console.log(error))
        .finally(() => {
          this.isDeletingPayment = false;
        });
    },
    async downloadInvoice() {
      this.isDownloading = true;
      const encryptedId = this.assignmentId || this.$route.params.id;
      try {
        const response = await axios.get(
          `/stock_assignments/${encryptedId}/invoice/download`,
          { responseType: "blob" }
        );
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `stock_assignment_${this.assignment.assignment_number}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.log(error);
      }
      this.isDownloading = false;
    },
  },
};
</script>
